/* import __COLOCATED_TEMPLATE__ from './conversation-tags-updated.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'ember-intl/services/intl';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type ConversationTagsUpdated from 'embercom/objects/inbox/renderable/conversation-tags-updated';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import {
  truncateEventText,
  isEventTextTruncated,
  FEATURE_FLAG_NAME,
} from 'embercom/lib/inbox2/event-text-truncation';

interface ConversationTagsUpdatedPart extends RenderablePart {
  renderableData: ConversationTagsUpdated;
}

interface Args {
  part: ConversationTagsUpdatedPart;
  conversation: Conversation;
}

interface Signature {
  Args: Args;
}

interface TagDisplayInfo {
  name: string;
  truncatedName: string;
  isTruncated: boolean;
}

export default class Inbox2ConversationStreamEventPartsConversationTagsUpdatedComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;

  get isConversationEventsUiImprovementsEnabled() {
    return this.session.workspace?.isFeatureEnabled(FEATURE_FLAG_NAME) ?? false;
  }

  get name() {
    return this.renderableData.taggingEntity.name;
  }

  get renderableData() {
    return this.args.part.renderableData;
  }

  get noTagsAdded() {
    return this.renderableData.tagsAdded === null || this.renderableData.tagsAdded.length === 0;
  }

  get noTagsRemoved() {
    return this.renderableData.tagsRemoved === null || this.renderableData.tagsRemoved.length === 0;
  }

  private getTagDisplayInfo(tagName: string): TagDisplayInfo {
    return {
      name: tagName,
      truncatedName: truncateEventText(tagName),
      isTruncated: isEventTextTruncated(tagName),
    };
  }

  get tagsAddedDisplayInfo(): TagDisplayInfo[] {
    if (this.noTagsAdded) {
      return [];
    }
    return this.renderableData.tagsAdded.map((tag) => this.getTagDisplayInfo(tag.name));
  }

  get tagsRemovedDisplayInfo(): TagDisplayInfo[] {
    if (this.noTagsRemoved) {
      return [];
    }
    return this.renderableData.tagsRemoved.map((tag) => this.getTagDisplayInfo(tag.name));
  }

  get isSingleTagAdded(): boolean {
    return this.tagsAddedCount === 1;
  }

  get isSingleTagRemoved(): boolean {
    return this.tagsRemovedCount === 1;
  }

  get firstAddedTag(): TagDisplayInfo {
    return this.tagsAddedDisplayInfo[0];
  }

  get firstRemovedTag(): TagDisplayInfo {
    return this.tagsRemovedDisplayInfo[0];
  }

  get includeAuthorForAdded(): boolean {
    return this.noTagsRemoved;
  }

  get tagsAddedCount(): number {
    return this.renderableData.tagsAdded?.length ?? 0;
  }

  get tagsRemovedCount(): number {
    return this.renderableData.tagsRemoved?.length ?? 0;
  }

  get removedTranslationKey(): string {
    if (!this.noTagsAdded) {
      return 'inbox.conversation-stream.conversation-tags.removed-lowercase';
    }
    return 'inbox.conversation-stream.conversation-tags.removed';
  }

  // Prefix for "Tag" or "Tags" before the tag names
  get tagsAddedPrefix(): string {
    if (this.noTagsAdded) {
      return '';
    }
    let count = this.tagsAddedCount;
    return this.intl.t('inbox.conversation-stream.conversation-tags.prefix', { count });
  }

  // Suffix for "added" or "added by {name}" after the tag names
  get tagsAddedSuffix(): string {
    if (this.noTagsAdded) {
      return '';
    }
    if (this.includeAuthorForAdded && this.name) {
      return this.intl.t('inbox.conversation-stream.conversation-tags.added-by', {
        name: this.name,
      });
    }
    return this.intl.t('inbox.conversation-stream.conversation-tags.added-suffix');
  }

  // Prefix for "Tag" or "Tags" (or lowercase) before the tag names
  get tagsRemovedPrefix(): string {
    if (this.noTagsRemoved) {
      return '';
    }
    let count = this.tagsRemovedCount;
    let isLowercase = !this.noTagsAdded;
    if (isLowercase) {
      return this.intl.t('inbox.conversation-stream.conversation-tags.prefix-lowercase', { count });
    }
    return this.intl.t('inbox.conversation-stream.conversation-tags.prefix', { count });
  }

  // Suffix for "removed by {name}" after the tag names
  get tagsRemovedSuffix(): string {
    if (this.noTagsRemoved) {
      return '';
    }
    return this.intl.t('inbox.conversation-stream.conversation-tags.removed-by', {
      name: this.name,
    });
  }

  get tagsAddedText(): string {
    if (this.noTagsAdded) {
      return '';
    }
    let tagsToBeAdded = this.renderableData.tagsAdded.map((tag) => `"${tag.name}"`).join(', ');
    let includeAuthor = this.noTagsRemoved;

    return this.intl.t('inbox.conversation-stream.conversation-tags.added', {
      tags: tagsToBeAdded,
      name: includeAuthor ? this.name : undefined,
      count: this.renderableData.tagsAdded.length,
    });
  }

  get tagsRemovedText(): string {
    if (this.noTagsRemoved) {
      return '';
    }
    let tagsToBeRemoved = this.renderableData.tagsRemoved.map((tag) => `"${tag.name}"`).join(', ');
    let translationKey = 'inbox.conversation-stream.conversation-tags.removed';
    let name = this.name;
    let count = this.renderableData.tagsRemoved.length;

    if (!this.noTagsAdded) {
      translationKey = 'inbox.conversation-stream.conversation-tags.removed-lowercase';
    }

    return this.intl.t(translationKey, {
      tags: tagsToBeRemoved,
      name,
      count,
    });
  }

  get textToDisplay() {
    let tagsAdded = this.tagsAddedText;
    let tagsRemoved = this.tagsRemovedText;

    if (this.noTagsAdded) {
      return tagsRemoved;
    }
    if (this.noTagsRemoved) {
      return tagsAdded;
    }

    return `${this.tagsAddedText}, ${this.intl.t('inbox.common.and')} ${this.tagsRemovedText}`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::ConversationTagsUpdated': typeof Inbox2ConversationStreamEventPartsConversationTagsUpdatedComponent;
    'inbox2/conversation-stream/event-parts/conversation-tags-updated': typeof Inbox2ConversationStreamEventPartsConversationTagsUpdatedComponent;
  }
}
